import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, ExternalLink, UserRound, CheckCheck } from "lucide-react";
import { useInbox } from "@/components/inbox/InboxProvider";
import { openWhatsAppWeb, useHandle, clientLine, timeAgo, isLate, waitLabel } from "@/components/inbox/inboxUtils";

const Row = ({ t, done, onDone, openClient }) => {
  const { busy, handle } = useHandle(onDone);
  const late = !done && isLate(t);
  return (
    <div data-testid={`thread-${t.wa_id}`} data-late={late} className={`flex flex-wrap items-center gap-4 rounded-xl border px-4 py-3 transition-colors ${late ? "border-red-500/50 bg-red-500/10" : "border-[#1F293D] bg-[#111827] hover:border-emerald-500/30"}`}>
      <MessageCircle className={`h-5 w-5 shrink-0 ${done ? "text-slate-500" : late ? "text-red-400" : "text-[#25D366]"}`} />
      <div className="min-w-[220px] flex-1">
        <div className="font-semibold text-white">{t.client?.name || t.phone}</div>
        <div className="text-xs text-slate-400">{t.phone} · {clientLine(t.client)}</div>
      </div>
      <div className="min-w-[200px] flex-[2] break-words text-sm text-slate-300 [overflow-wrap:anywhere]">
        “{t.last_text}”
        <span className="ml-2 text-[11px] text-slate-500">{timeAgo(t.last_at)}{t.count > 1 ? ` · ${t.count} msgs` : ""}</span>
        {!done && <div data-testid={`thread-wait-${t.wa_id}`} className={`text-[11px] ${late ? "font-semibold text-red-400" : "text-slate-500"}`}>{late ? "⚠ " : ""}{waitLabel(t)}</div>}
        {done && <div className="text-[11px] text-slate-500">Atendido {timeAgo(t.handled_at)}{t.handled_by ? ` por ${t.handled_by}` : ""}</div>}
      </div>
      <div className="flex gap-2">
        <button data-testid={`thread-call-${t.wa_id}`} onClick={() => openWhatsAppWeb(t)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-xs font-bold text-slate-950"><ExternalLink className="h-3.5 w-3.5" /> WhatsApp Web</button>
        <button data-testid={`thread-open-${t.wa_id}`} disabled={!t.client_id} onClick={() => openClient(t.client_id)} className="rounded-lg border border-[#1F293D] p-2 text-slate-300 hover:bg-[#1A2234] disabled:opacity-40" title="Abrir ficha"><UserRound className="h-4 w-4" /></button>
        {!done && <button data-testid={`thread-handle-${t.wa_id}`} disabled={busy} onClick={() => handle(t)} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"><CheckCheck className="h-3.5 w-3.5" /> Atendido</button>}
      </div>
    </div>
  );
};

export default function Responses() {
  const inbox = useInbox();
  const [tab, setTab] = useState("open");
  const [handled, setHandled] = useState([]);
  const loadHandled = () => api.get("/wa/threads", { params: { status: "handled" } }).then((r) => setHandled(r.data.items));
  useEffect(() => { if (tab === "handled") loadHandled(); }, [tab]);
  const byWait = (a, b) => new Date(a.open_since || a.last_at) - new Date(b.open_since || b.last_at);
  const items = tab === "open" ? [...inbox.items].sort(byWait) : handled;
  const lateN = inbox.items.filter(isLate).length;
  return (
    <div data-testid="responses-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Respostas</h1>
          <p className="mt-2 text-sm text-slate-400">Clientes que escreveram no WhatsApp Financeiro. Retorne pelo WhatsApp do Suporte.</p>
        </div>
      </div>
      <div className="flex gap-2">
        {[["open", `Pendentes (${inbox.open})`], ["handled", "Atendidos"]].map(([k, l]) => (
          <button key={k} data-testid={`responses-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "border border-[#1F293D] text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "open" && lateN > 0 && <div data-testid="responses-late-alert" className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300">{lateN} {lateN === 1 ? "cliente aguardando" : "clientes aguardando"} há mais de 30 minutos. Priorize esses atendimentos.</div>}
      <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-3 text-xs text-sky-200">
        Deixe o WhatsApp do <b>Suporte</b> conectado na aba do WhatsApp Web que o botão abre. As próximas conversas abrem sempre nessa mesma aba.
      </div>
      <div data-testid="responses-list" className="space-y-2">
        {items.map((t) => <Row key={t.wa_id} t={t} done={tab === "handled"} onDone={() => { inbox.reload(); loadHandled(); }} openClient={inbox.openClient} />)}
        {!items.length && <div data-testid="responses-empty" className="rounded-xl border border-dashed border-[#1F293D] py-12 text-center text-sm text-slate-500">{tab === "open" ? "Nenhuma resposta pendente." : "Nenhuma conversa atendida ainda."}</div>}
      </div>
    </div>
  );
}
